import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, Legend,
} from 'recharts';
import {
  Users, Wrench, Package, Shield, Truck, UserCheck, Car,
  Clock, CheckCircle, XCircle, Activity,
  FileText, Award, Send, ThumbsUp,
  Banknote, Hourglass, Landmark,
  ShieldCheck, ClipboardCheck, Droplet, X, ChevronRight,
} from 'lucide-react';
import API_URL from '../../api/baseURL';
import FleetMapPanel from '../../components/OwnerDashboard/FleetMapPanel';
import { getAllFleetPositions } from '../../api/ownerTelematics';

/* ─── Helpers ──────────────────────────────────────────── */
const fmt = (n) => Number(n || 0).toLocaleString('fr-FR');
const fcfa = (n) => `${fmt(n)} FCFA`;

const COLORS = ['#3b82f6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#64748b'];

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-sm shadow-xl">
      <p className="text-white font-medium">{payload[0].name}</p>
      <p className="text-cyan-300">{fmt(payload[0].value)}</p>
    </div>
  );
};

/* ─── Skeleton ─────────────────────────────────────────── */
const SkeletonCard = () => (
  <div className="rounded-2xl p-6 bg-slate-800/60 animate-pulse">
    <div className="h-4 w-24 bg-slate-700 rounded mb-3" />
    <div className="h-8 w-16 bg-slate-700 rounded" />
  </div>
);

const SkeletonSection = () => (
  <div>
    <div className="h-6 w-40 bg-slate-700 rounded mb-4 animate-pulse" />
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {[1, 2, 3, 4].map(i => <SkeletonCard key={i} />)}
    </div>
  </div>
);

/* ─── Stat card (inline — matches existing dark theme) ── */
const Card = ({ icon: Icon, label, value, gradient, sub }) => (
  <div className={`relative overflow-hidden rounded-2xl p-5 ${gradient} shadow-lg`}>
    <div className="flex items-start justify-between">
      <div>
        <p className="text-white/70 text-xs font-medium uppercase tracking-wide mb-1">{label}</p>
        <p className="text-2xl font-bold text-white">{value}</p>
        {sub && <p className="text-white/50 text-xs mt-1">{sub}</p>}
      </div>
      <Icon className="w-8 h-8 text-white/30" />
    </div>
    <div className="absolute -bottom-4 -right-4 w-20 h-20 bg-white/5 rounded-full blur-xl" />
  </div>
);

/* ─── Section wrapper ──────────────────────────────────── */
const Section = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="text-lg font-semibold text-slate-300 mb-4 border-b border-slate-800 pb-2">{title}</h2>
    {children}
  </section>
);

/* ─── Clickable stat card (for the fleet widgets) ──────── */
const ClickableCard = ({ icon: Icon, label, value, sub, gradient, onClick }) => (
  <button
    onClick={onClick}
    className={`relative overflow-hidden rounded-2xl p-5 ${gradient} shadow-lg text-left w-full hover:brightness-110 hover:scale-[1.02] transition-all`}
  >
    <div className="flex items-start justify-between">
      <div>
        <p className="text-white/70 text-xs font-medium uppercase tracking-wide mb-1">{label}</p>
        <p className="text-2xl font-bold text-white">{value}</p>
        {sub && <p className="text-white/50 text-xs mt-1">{sub}</p>}
      </div>
      <Icon className="w-8 h-8 text-white/30" />
    </div>
    <div className="flex items-center gap-1 text-white/60 text-xs mt-3 font-medium">
      Voir les détails <ChevronRight className="w-3.5 h-3.5" />
    </div>
    <div className="absolute -bottom-4 -right-4 w-20 h-20 bg-white/5 rounded-full blur-xl" />
  </button>
);

/* ─── Fleet detail modal ───────────────────────────────── */
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('fr-FR') : '—');

const FleetModal = ({ open, onClose, title, children }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
          <h3 className="text-lg font-bold text-white">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="overflow-y-auto p-6">{children}</div>
      </div>
    </div>
  );
};

/* One company group with inline rename/merge */
const CompanyGroupRow = ({ group, onMerge }) => {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(group.company);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    const to = name.trim();
    if (!to || to === group.company) { setEditing(false); return; }
    setBusy(true);
    try {
      await onMerge(to); // parent already binds the "from" (this group's company)
    } finally {
      setBusy(false);
      setEditing(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2 gap-2">
        {editing ? (
          <div className="flex items-center gap-2 flex-1">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="bg-slate-800 border border-slate-600 rounded-lg px-2 py-1 text-sm text-white flex-1"
              autoFocus
            />
            <button onClick={save} disabled={busy} className="text-xs bg-cyan-600 hover:bg-cyan-500 text-white px-3 py-1 rounded-lg disabled:opacity-50">
              {busy ? '...' : 'Enregistrer'}
            </button>
            <button onClick={() => { setEditing(false); setName(group.company); }} className="text-xs text-slate-400 hover:text-white px-2">Annuler</button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <h4 className="text-cyan-300 font-semibold">{group.company}</h4>
            <button onClick={() => setEditing(true)} className="text-xs text-slate-500 hover:text-cyan-300 underline">renommer / fusionner</button>
          </div>
        )}
        <span className="text-xs bg-slate-800 text-slate-300 px-2 py-1 rounded-full shrink-0">{group.count} véhicule{group.count > 1 ? 's' : ''}</span>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-slate-500 text-left border-b border-slate-800">
            <th className="py-2 font-medium">Plaque</th>
            <th className="py-2 font-medium">Véhicule</th>
            <th className="py-2 font-medium">Propriétaire</th>
            <th className="py-2 font-medium">Expiration</th>
          </tr>
        </thead>
        <tbody>
          {group.cars.map((c) => (
            <tr key={c._id || c.plateNumber} className="border-b border-slate-800/60 text-slate-200">
              <td className="py-2 font-bold">{c.plateNumber || '—'}</td>
              <td className="py-2 text-slate-400">{[c.brand, c.model].filter(Boolean).join(' ') || '—'}</td>
              <td className="py-2 text-slate-400">{c.owner || '—'}</td>
              <td className="py-2 text-slate-400">{fmtDate(c.expiry)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/* Grouped-by-company list (insurance / technical check) with merge support */
const CompanyGroups = ({ groups, onMerge }) => {
  if (!groups?.length) return <p className="text-slate-400 text-center py-8">Aucune donnée.</p>;
  return (
    <div className="space-y-6">
      <p className="text-xs text-slate-500">
        Astuce : pour regrouper des doublons (ex. « sicta » et « SICTA »), cliquez « renommer / fusionner » et donnez le nom exact à conserver.
      </p>
      {groups.map((g) => (
        <CompanyGroupRow key={g.company} group={g} onMerge={(to) => onMerge(g.company, to)} />
      ))}
    </div>
  );
};

/* Overdue oil-change list */
const OilChangeList = ({ cars }) => {
  if (!cars?.length) return <p className="text-slate-400 text-center py-8">Aucun véhicule en retard de vidange.</p>;
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-slate-500 text-left border-b border-slate-800">
          <th className="py-2 font-medium">Plaque</th>
          <th className="py-2 font-medium">Véhicule</th>
          <th className="py-2 font-medium">Propriétaire</th>
          <th className="py-2 font-medium text-right">Compteur</th>
          <th className="py-2 font-medium text-right">Vidange prévue</th>
          <th className="py-2 font-medium text-right">Dépassement</th>
        </tr>
      </thead>
      <tbody>
        {cars.map((c) => (
          <tr key={c._id || c.plateNumber} className="border-b border-slate-800/60 text-slate-200">
            <td className="py-2 font-bold">{c.plateNumber || '—'}</td>
            <td className="py-2 text-slate-400">{[c.brand, c.model].filter(Boolean).join(' ') || '—'}</td>
            <td className="py-2 text-slate-400">{c.owner || '—'}</td>
            <td className="py-2 text-slate-400 text-right">{fmt(c.odometerReading)} km</td>
            <td className="py-2 text-slate-400 text-right">{fmt(c.nextOilChangeOdometer)} km</td>
            <td className="py-2 text-right font-bold text-red-400">+{fmt(c.kmOver)} km</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

/* ═══════════════════════════════════════════════════════════
   Main component
═══════════════════════════════════════════════════════════ */
export default function AdminDashboard() {
  const token = localStorage.getItem('authToken');

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-dashboard-stats'],
    queryFn: async () => {
      const res = await axios.get(`${API_URL}/admin/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return res.data.data;
    },
    refetchInterval: 60_000, // refresh every minute
  });

  // Fleet insights (cars by insurance / technical-check company, overdue oil changes)
  const { data: fleet } = useQuery({
    queryKey: ['admin-fleet-insights'],
    queryFn: async () => {
      const res = await axios.get(`${API_URL}/admin/fleet/insights`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return res.data.data;
    },
    refetchInterval: 60_000,
  });

  const [fleetModal, setFleetModal] = useState(null); // 'insurance' | 'technical' | 'oil' | null
  const queryClient = useQueryClient();

  // Merge/rename a company value across all vehicles, then refresh the widgets
  const mergeCompany = (type) => async (from, to) => {
    try {
      await axios.patch(
        `${API_URL}/admin/fleet/merge-company`,
        { type, from, to },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      await queryClient.invalidateQueries({ queryKey: ['admin-fleet-insights'] });
    } catch (e) {
      alert(e.response?.data?.error || 'Échec de la fusion');
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-10">
        <h1 className="text-2xl font-bold text-white">Dashboard</h1>
        {[1, 2, 3, 4].map(i => <SkeletonSection key={i} />)}
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-400 bg-red-500/10 rounded-xl p-6 text-center">
        Failed to load dashboard stats. Please try again.
      </div>
    );
  }

  const { users, vehicles, requests, quotations, invoices } = data;
  const wq = data.workshopQuotations || {};
  const wqEmpty = { count: 0, amount: 0 };
  const wi = data.workshopInvoices || {};
  const wiEmpty = { count: 0, amount: 0, platformRevenue: 0 };
  const ratios = data.ratios || {};

  /* ── Chart data ── */
  const requestPie = [
    { name: 'Pending', value: requests.pending },
    { name: 'Accepted', value: requests.accepted },
    { name: 'In Progress', value: requests.inProgress },
    { name: 'Completed', value: requests.completed },
    { name: 'Declined', value: requests.declined },
    { name: 'Cancelled', value: requests.cancelled },
  ].filter(d => d.value > 0);

  // Devis amount by status (client-facing FCFA)
  const wqBar = [
    { name: 'En attente', amount: (wq.pending   || wqEmpty).amount },
    { name: 'Révisés',    amount: (wq.revised   || wqEmpty).amount },
    { name: 'Contre-off.', amount: (wq.countered || wqEmpty).amount },
    { name: 'Acceptés',   amount: (wq.accepted  || wqEmpty).amount },
    { name: 'Confirmés',  amount: (wq.confirmed || wqEmpty).amount },
    { name: 'Rejetés',    amount: (wq.rejected  || wqEmpty).amount },
  ];

  const invoiceBar = [
    { name: 'Payées', amount: (wi.paid || wiEmpty).amount },
    { name: 'Impayées', amount: (wi.unpaid || wiEmpty).amount },
    { name: 'Revenu plateforme', amount: wi.platformRevenuePaid || 0 },
  ];

  return (
    <div className="space-y-2">
      <h1 className="text-2xl font-bold text-white mb-6">Dashboard Overview</h1>

      {/* ════════ USERS & VEHICLES ════════ */}
      <Section title="Users & Vehicles">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <Card icon={Users}     label="Car Owners"  value={fmt(users.carOwners)}  gradient="bg-gradient-to-br from-blue-600 to-blue-900" />
          <Card icon={Wrench}    label="Workshops"   value={fmt(users.workshops)}  gradient="bg-gradient-to-br from-indigo-600 to-indigo-900" />
          <Card icon={Package}   label="Suppliers"   value={fmt(users.suppliers)}  gradient="bg-gradient-to-br from-violet-600 to-violet-900" />
          <Card icon={Car}       label="Total Cars"  value={fmt(vehicles?.total)}  gradient="bg-gradient-to-br from-orange-600 to-orange-900" />
          <Card icon={UserCheck} label="Total Users" value={fmt(users.total)}      gradient="bg-gradient-to-br from-cyan-600 to-cyan-900"
                sub={`${fmt(users.insuranceCompanies)} insurance  ·  ${fmt(users.vehicleLoaders)} loaders`} />
        </div>
      </Section>

      {/* ════════ LIVE GPS ════════ */}
      {/* Same component as the owner dashboard, pointed at the platform-wide
          endpoint. Renders nothing until at least one device is linked. */}
      <Section title="Suivi GPS en direct">
        <FleetMapPanel
          token={token}
          fetcher={getAllFleetPositions}
          title="Véhicules équipés — plateforme"
          canRequestDevice={false}
          onOpenVehicle={(r) => window.open(`/vehicles/${r.vehicleId}`, '_blank')}
        />
      </Section>

      {/* ════════ FLEET (clickable) ════════ */}
      <Section title="Parc Automobile">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <ClickableCard
            icon={ShieldCheck}
            label="Voitures par assurance"
            value={fmt((fleet?.byInsurance || []).reduce((s, g) => s + g.count, 0))}
            sub={`${fmt((fleet?.byInsurance || []).length)} compagnie(s)`}
            gradient="bg-gradient-to-br from-blue-600 to-blue-900"
            onClick={() => setFleetModal('insurance')}
          />
          <ClickableCard
            icon={ClipboardCheck}
            label="Voitures par contrôle technique"
            value={fmt((fleet?.byTechnicalCheck || []).reduce((s, g) => s + g.count, 0))}
            sub={`${fmt((fleet?.byTechnicalCheck || []).length)} centre(s)`}
            gradient="bg-gradient-to-br from-teal-600 to-teal-900"
            onClick={() => setFleetModal('technical')}
          />
          <ClickableCard
            icon={Droplet}
            label="Vidange en retard"
            value={fmt((fleet?.dueOilChange || []).length)}
            sub="voiture(s) à relancer"
            gradient="bg-gradient-to-br from-rose-600 to-rose-900"
            onClick={() => setFleetModal('oil')}
          />
        </div>
      </Section>

      {/* ════════ REQUESTS ════════ */}
      <Section title="Maintenance Requests">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card icon={Clock}       label="Pending"     value={fmt(requests.pending)}    gradient="bg-gradient-to-br from-amber-600 to-amber-900" />
          <Card icon={CheckCircle} label="Accepted"    value={fmt(requests.accepted)}   gradient="bg-gradient-to-br from-emerald-600 to-emerald-900" />
          <Card icon={XCircle}     label="Declined"    value={fmt(requests.declined)}   gradient="bg-gradient-to-br from-red-600 to-red-900" />
          <Card icon={Activity}    label="In Progress" value={fmt(requests.inProgress)} gradient="bg-gradient-to-br from-sky-600 to-sky-900"
                sub={`${fmt(requests.completed)} completed  ·  ${fmt(requests.total)} total`} />
        </div>
        {requestPie.length > 0 && (
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6">
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={requestPie} cx="50%" cy="50%" innerRadius={60} outerRadius={100} paddingAngle={3} dataKey="value" label={({ name, value }) => `${name} (${value})`}>
                  {requestPie.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ color: '#94a3b8', fontSize: 13 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </Section>

      {/* ════════ QUOTATIONS (Devis) ════════ */}
      <Section title={`Devis — ${fmt(wq.totalCount || 0)} au total · ${fcfa(wq.totalAmount || 0)}`}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          <Card icon={Clock}       label="En attente" value={fcfa((wq.pending   || wqEmpty).amount)} sub={`${fmt((wq.pending   || wqEmpty).count)} devis`} gradient="bg-gradient-to-br from-amber-600 to-amber-900" />
          <Card icon={Activity}    label="Révisés"    value={fcfa((wq.revised   || wqEmpty).amount)} sub={`${fmt((wq.revised   || wqEmpty).count)} devis`} gradient="bg-gradient-to-br from-yellow-600 to-yellow-900" />
          <Card icon={Send}        label="Contre-offres" value={fcfa((wq.countered || wqEmpty).amount)} sub={`${fmt((wq.countered || wqEmpty).count)} devis`} gradient="bg-gradient-to-br from-purple-600 to-purple-900" />
          <Card icon={ThumbsUp}    label="Acceptés"   value={fcfa((wq.accepted  || wqEmpty).amount)} sub={`${fmt((wq.accepted  || wqEmpty).count)} devis`} gradient="bg-gradient-to-br from-emerald-600 to-emerald-900" />
          <Card icon={CheckCircle} label="Confirmés"  value={fcfa((wq.confirmed || wqEmpty).amount)} sub={`${fmt((wq.confirmed || wqEmpty).count)} devis`} gradient="bg-gradient-to-br from-green-600 to-green-900" />
          <Card icon={XCircle}     label="Rejetés"    value={fcfa((wq.rejected  || wqEmpty).amount)} sub={`${fmt((wq.rejected  || wqEmpty).count)} devis`} gradient="bg-gradient-to-br from-red-600 to-red-900" />
        </div>
        {wqBar.some(d => d.amount > 0) && (
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={wqBar} barSize={42}>
                <XAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 12 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: '#94a3b8', fontSize: 12 }} axisLine={false} tickLine={false} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="amount" radius={[8, 8, 0, 0]}>
                  {wqBar.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Section>

      {/* ════════ PERFORMANCE (ratios) ════════ */}
      <Section title="Performance">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Card
            icon={ThumbsUp}
            label="Taux d'acceptation des devis"
            value={`${fmt(ratios.quotationAcceptanceRate || 0)} %`}
            sub={`${fmt((wq.accepted || wqEmpty).count + (wq.confirmed || wqEmpty).count)} acceptés sur ${fmt(wq.totalCount || 0)} devis`}
            gradient="bg-gradient-to-br from-emerald-600 to-emerald-900"
          />
          <Card
            icon={Banknote}
            label="Taux de paiement des factures"
            value={`${fmt(ratios.invoicePaidRate || 0)} %`}
            sub={`${fmt((wi.paid || wiEmpty).count)} payées sur ${fmt(wi.totalCount || 0)} factures`}
            gradient="bg-gradient-to-br from-teal-600 to-teal-900"
          />
        </div>
      </Section>

      {/* ════════ INVOICES & FINANCIALS ════════ */}
      <Section title={`Factures — ${fmt(wi.totalCount || 0)} au total · ${fcfa(wi.totalAmount || 0)}`}>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <Card icon={Banknote}  label="Payées"            value={fcfa((wi.paid || wiEmpty).amount)}   gradient="bg-gradient-to-br from-emerald-600 to-emerald-900"
                sub={`${fmt((wi.paid || wiEmpty).count)} factures`} />
          <Card icon={Hourglass} label="Impayées"          value={fcfa((wi.unpaid || wiEmpty).amount)} gradient="bg-gradient-to-br from-yellow-600 to-yellow-900"
                sub={`${fmt((wi.unpaid || wiEmpty).count)} factures`} />
          <Card icon={Landmark}  label="Revenu plateforme" value={fcfa(wi.platformRevenuePaid || 0)}    gradient="bg-gradient-to-br from-teal-600 to-teal-900"
                sub="commission 10% (factures payées)" />
        </div>
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={invoiceBar} barSize={48}>
              <XAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 13 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#94a3b8', fontSize: 12 }} axisLine={false} tickLine={false} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="amount" radius={[8, 8, 0, 0]}>
                {invoiceBar.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Section>

      {/* ════════ FLEET MODALS ════════ */}
      <FleetModal open={fleetModal === 'insurance'} onClose={() => setFleetModal(null)} title="Voitures par compagnie d'assurance">
        <CompanyGroups groups={fleet?.byInsurance} onMerge={mergeCompany('insurance')} />
      </FleetModal>
      <FleetModal open={fleetModal === 'technical'} onClose={() => setFleetModal(null)} title="Voitures par centre de contrôle technique">
        <CompanyGroups groups={fleet?.byTechnicalCheck} onMerge={mergeCompany('technicalCheck')} />
      </FleetModal>
      <FleetModal open={fleetModal === 'oil'} onClose={() => setFleetModal(null)} title="Vidanges en retard">
        <OilChangeList cars={fleet?.dueOilChange} />
      </FleetModal>
    </div>
  );
}
